import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const useSenhasApi = () => {
  // Estados para os dados
  const [senhaAtual, setSenhaAtual] = useState(null);
  const [guicheAtual, setGuicheAtual] = useState(null);
  const [preferencialAtual, setPreferencialAtual] = useState(false);
  const [ultimasSenhas, setUltimasSenhas] = useState([]);
  const [dataChamadaAtual, setDataChamadaAtual] = useState(null);
  
  // Estado para controlar o carregamento e erros
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  
  // Referência para armazenar a última resposta recebida
  const lastDataRef = useRef(null);
  
  // Referência para o intervalo
  const intervalRef = useRef(null);

  // Função para formatar a data de chamada
  const formatarDataChamada = (dataChamada) => {
    if (!dataChamada) return '';
    
    try {
      const data = parseISO(dataChamada);
      return format(data, 'HH:mm:ss', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  // Função para processar dados da API
  const processarDadosAPI = useCallback((data) => {
    try {
      if (!data || !data.success) {
        console.warn("Dados da API inválidos:", data);
        return false;
      }
      
      // Verificar se os dados são idênticos aos já processados
      if (lastDataRef.current && 
          JSON.stringify(lastDataRef.current) === JSON.stringify(data)) {
        return true; // Dados não mudaram, não atualizar estado
      }
      
      // Armazenar os novos dados na referência
      lastDataRef.current = data;
      
      // Obtém as senhas formatadas da resposta
      const senhasChamadas = data.data || [];
      
      if (senhasChamadas.length === 0) {
        console.warn("Nenhuma senha chamada encontrada");
        setSenhaAtual(null);
        setGuicheAtual(null);
        setPreferencialAtual(false);
        setDataChamadaAtual(null);
        setUltimasSenhas([]);
        return true;
      }
      
      // As senhas já vêm ordenadas pela API (mais recentes primeiro)
      
      // Define a senha atual (mais recente)
      const senhaAtualData = senhasChamadas[0];
      if (senhaAtualData) {
        // Log para debug de inconsistências
        console.log('[DEBUG] Senha atual processada:', {
          numero: senhaAtualData.senha,
          preferencial_banco: senhaAtualData.preferencial,
          preferencial_processado: Boolean(senhaAtualData?.preferencial),
          timestamp: new Date().toISOString()
        });
        
        setSenhaAtual(senhaAtualData.senha);
        setGuicheAtual(senhaAtualData.guiche);
        // Usa o campo preferencial do banco de dados com validação defensiva
        setPreferencialAtual(Boolean(senhaAtualData?.preferencial));
        // Formata e armazena o horário de chamada
        setDataChamadaAtual(formatarDataChamada(senhaAtualData.chamada_em));
      }
      
      // Define as últimas senhas chamadas (excluindo a primeira, que já é a atual)
      const ultimasSenhasData = senhasChamadas.slice(1, 5); // Pega até 4 senhas adicionais (além da atual)
      setUltimasSenhas(ultimasSenhasData.map(senha => ({
        senha: senha.senha,
        guiche: senha.guiche,
        preferencial: senha.preferencial,
        horario: formatarDataChamada(senha.chamada_em),
        status: senha.status // Incluir o status da senha
      })));
      
      return true;
    } catch (error) {
      console.error("Erro ao processar dados da API:", error);
      return false;
    }
  }, []);

  // Função para buscar dados da API
  const fetchDados = useCallback(async () => {
    // Não ativar o estado de carregamento para atualizações periódicas
    // isto ajuda a evitar renderizações desnecessárias
    const isInitialLoad = carregando;
    
    if (isInitialLoad) {
      setCarregando(true);
    }
    
    try {
      // Buscar senhas chamadas usando a instância api configurada
      const response = await api.get('/senhas/chamadas', { 
        timeout: 8000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.status === 200 && response.data) {
        // Usar os dados da API se o processamento for bem-sucedido
        processarDadosAPI(response.data);
        
        // Só atualizar o estado de erro se ele existir
        if (erro) {
          setErro(null);
        }
      } else {
        console.warn("Resposta da API inválida:", response);
        setErro("Resposta da API inválida");
        // Limpar os dados quando não há resposta válida
        setSenhaAtual(null);
        setGuicheAtual(null);
        setPreferencialAtual(false);
        setDataChamadaAtual(null);
        setUltimasSenhas([]);
      }
    } catch (error) {
      console.error("Erro ao buscar dados da API:", error);
      setErro(`Erro ao conectar ao servidor: ${error.message}`);
      // Limpar os dados quando ocorre um erro
      setSenhaAtual(null);
      setGuicheAtual(null);
      setPreferencialAtual(false);
      setDataChamadaAtual(null);
      setUltimasSenhas([]);
    } finally {
      if (isInitialLoad) {
        setCarregando(false);
      }
    }
  }, [carregando, erro, processarDadosAPI]);

  // Efeito para buscar os dados na montagem
  useEffect(() => {
    // Primeira busca
    fetchDados();
    
    // Configurar intervalo para busca periódica (a cada 2 segundos)
    intervalRef.current = setInterval(fetchDados, 2000);
    
    // Limpar intervalo ao desmontar
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchDados]);

  return {
    senhaAtual,
    guicheAtual,
    preferencialAtual,
    dataChamadaAtual,
    ultimasSenhas,
    carregando,
    erro,
    fetchDados
  };
};

export default useSenhasApi; 